// 學生上傳的「被拒原因」與「額度」（kanban 1000873）。
//
// 上傳頁的流程是「先跳回列表、再送出」（UploadEssay），送出被後端拒絕時人已不在
// 上傳頁，錯誤沒有任何地方顯示——不只額度（400「超過使用次數限制」），任何拒絕都會
// 靜默。這裡負責：把原因取出來、交給列表頁（取走即清，只跳一次）、以及與後端同一口徑
// 的額度判斷（composition-api make_ticket：學生在該書的紀錄數 >= usedLimit 就拒絕，
// 不分班級）。

export const SUBMIT_ERROR_KEY = "pendingSubmissionError";
export const DEFAULT_USED_LIMIT = 12; // 書籍資料讀不到 usedLimit 時的既有備用值
export const FALLBACK_SUBMIT_ERROR = "提交失敗，請稍後再試";
// 5MB 是後端代理層的實測上限（見 batchFileScreen.test.ts），不隨前端門檻改
export const FILE_TOO_LARGE_MESSAGE = "上傳的檔案過大，請上傳小於 5MB 的圖片或 PDF";

type ErrorLike = {
  response?: { status?: number; data?: { detail?: unknown; message?: unknown } };
  message?: unknown;
};

/**
 * 從 axios 錯誤／一般 Error 取出可給學生看的拒絕原因。
 * 優先序與上傳頁既有的文案對照一致：413 → 後端帶回的 detail / message →
 * 依錯誤訊息判斷網路／逾時／403／500 → 備用文案。axios 的原始英文訊息
 * （"Request failed with status code 500"、"Network Error"）不直接給學生看。
 */
export const extractSubmitErrorMessage = (error: unknown): string => {
  if (!error || typeof error !== "object") return FALLBACK_SUBMIT_ERROR;
  const err = error as ErrorLike;
  const status = err.response?.status;
  if (status === 413) return FILE_TOO_LARGE_MESSAGE;
  const detail = err.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  const message = err.response?.data?.message;
  if (typeof message === "string" && message.trim()) return message;

  const raw = typeof err.message === "string" ? err.message : "";
  if (/network/i.test(raw)) return "網路連線問題，請檢查您的網路連線後再試一次";
  if (/timeout/i.test(raw)) return "伺服器回應時間過長，請稍後再試";
  if (status === 403 || raw.includes("403")) return "存取權限驗證失敗，請重新整理頁面後再試";
  if (raw.includes("413")) return FILE_TOO_LARGE_MESSAGE;
  if ((status !== undefined && status >= 500) || raw.includes("500")) {
    return "伺服器處理請求時發生錯誤，請稍後再試";
  }
  return FALLBACK_SUBMIT_ERROR;
};

type StorageLike = Pick<Storage, "getItem" | "setItem" | "removeItem">;

const defaultStorage = (): StorageLike | null => {
  try {
    return typeof sessionStorage !== "undefined" ? sessionStorage : null;
  } catch {
    return null;
  }
};

/** 上傳頁送出失敗時暫存原因，交給列表頁顯示 */
export const stashSubmitError = (message: string, storage: StorageLike | null = defaultStorage()) => {
  if (!storage || !message) return;
  storage.setItem(SUBMIT_ERROR_KEY, message);
};

/** 列表頁取走暫存的原因；取走即清，同一個原因只跳一次 */
export const takeSubmitError = (storage: StorageLike | null = defaultStorage()): string | null => {
  if (!storage) return null;
  const message = storage.getItem(SUBMIT_ERROR_KEY);
  if (message) storage.removeItem(SUBMIT_ERROR_KEY);
  return message || null;
};

export type QuotaState = {
  /** 學生在該書所有班級的紀錄總數；null = 讀不到（不顯示數字、不停用） */
  used: number | null;
  limit: number;
  reached: boolean;
};

/**
 * 額度狀態。used 是學生在該書所有班級的紀錄總數（與後端同口徑）。
 * - used 為 null（總數請求失敗）：不冒充 0，顯示「-」且不停用，交給後端把關。
 * - limit 缺或 <= 0：沿用既有備用值 12（與原本 `|| 12` 的行為一致）。
 */
export const quotaState = (used: number | null | undefined, limit: number | undefined): QuotaState => {
  const l = limit && limit > 0 ? limit : DEFAULT_USED_LIMIT;
  if (used === null) return { used: null, limit: l, reached: false };
  const u = used ?? 0;
  return { used: u, limit: l, reached: u >= l };
};

/**
 * 一次撈本書全部紀錄（額度用），列表只顯示目前班級的那些。
 * 後端帶 classCode 時就是精確比對 classCode；不帶（沒選到班）則全列，這裡照同一規則。
 */
export const filterRecordsByClass = <T extends { classCode?: string }>(
  records: T[],
  classCode: string | undefined
): T[] => (classCode ? records.filter((r) => r.classCode === classCode) : records);
